/* dirtlej Trips interaction layer
   --------------------------------------------------------------------------
   Keep visual values in trips.css and content/settings in Liquid schemas.

   Components:
   - TripsGallery: drag/scroll gallery + accessible lightbox
   - TripsContactModal: reusable Shopify Forms dialog
   - TripsCompare: mobile comparison tabs
   - TripsUI: reveal, route, topography and performant parallax lifecycle

   Motion is progressive enhancement. Every component works without animation,
   respects Reduce Motion and re-initializes after Shopify section reloads.
   -------------------------------------------------------------------------- */

if (!customElements.get('trips-gallery')) {
  customElements.define('trips-gallery', class TripsGallery extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.stage = this.querySelector('.trips-gallery__stage');
      this.items = Array.from(this.querySelectorAll('[data-gallery-open]'));
      this.dialog = this.querySelector('.trips-gallery-lightbox');
      this.lightboxImage = this.querySelector('.trips-gallery-lightbox__image');
      this.lightboxCaption = this.querySelector('[data-gallery-lightbox-caption]');
      this.lightboxCurrent = this.querySelector('[data-gallery-lightbox-current]');
      this.mobileCurrent = this.querySelector('[data-gallery-current]');
      this.scrollPrevious = this.querySelector('[data-gallery-scroll="previous"]');
      this.scrollNext = this.querySelector('[data-gallery-scroll="next"]');
      this.activeIndex = 0;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.items.forEach((item, index) => {
        item.addEventListener('click', () => {
          if (this.galleryDragged) {
            this.galleryDragged = false;
            return;
          }
          this.open(index);
        });
      });

      if (this.stage) {
        let dragging = false;
        let startX = 0;
        let startScrollLeft = 0;
        let activePointerId = null;

        this.stage.addEventListener('dragstart', (event) => event.preventDefault());
        this.stage.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'touch') return;
          this.isProgrammaticScroll = false;
          window.clearTimeout(this.programmaticScrollTimer);
          dragging = true;
          this.galleryDragged = false;
          startX = event.clientX;
          startScrollLeft = this.stage.scrollLeft;
          activePointerId = event.pointerId;
          this.stage.classList.add('is-dragging');
        });
        this.stage.addEventListener('pointermove', (event) => {
          if (!dragging) return;
          const distance = event.clientX - startX;
          if (Math.abs(distance) > 5) {
            this.galleryDragged = true;
            if (activePointerId !== null && !this.stage.hasPointerCapture?.(activePointerId)) {
              this.stage.setPointerCapture?.(activePointerId);
            }
          }
          const isRtl = window.getComputedStyle(this.stage).direction === 'rtl';
          this.stage.scrollLeft = startScrollLeft + (isRtl ? distance : -distance);
        });
        const stopDragging = (event) => {
          if (!dragging) return;
          dragging = false;
          this.stage.classList.remove('is-dragging');
          if (activePointerId !== null && this.stage.hasPointerCapture?.(activePointerId)) this.stage.releasePointerCapture(activePointerId);
          activePointerId = null;
        };
        this.stage.addEventListener('pointerup', stopDragging);
        this.stage.addEventListener('pointercancel', stopDragging);
        this.stage.addEventListener('pointerleave', stopDragging);
      }

      this.querySelector('[data-gallery-close]')?.addEventListener('click', () => this.close());
      this.querySelector('[data-gallery-previous]')?.addEventListener('click', () => this.show(this.activeIndex - 1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.show(this.activeIndex + 1));
      this.scrollPrevious?.addEventListener('click', () => this.scrollTo(this.activeIndex - 1));
      this.scrollNext?.addEventListener('click', () => this.scrollTo(this.activeIndex + 1));

      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog?.addEventListener('close', () => {
        document.documentElement.classList.remove('trips-gallery-open');
        this.lastOpener?.focus?.({ preventScroll: true });
        this.lastOpener = null;
      });

      this.dialog?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.show(this.activeIndex - 1);
        if (event.key === 'ArrowRight') this.show(this.activeIndex + 1);
      });

      let touchStartX = 0;
      let touchStartY = 0;
      this.dialog?.addEventListener('touchstart', (event) => {
        touchStartX = event.changedTouches[0]?.clientX || 0;
        touchStartY = event.changedTouches[0]?.clientY || 0;
      }, { passive: true });
      this.dialog?.addEventListener('touchend', (event) => {
        const touch = event.changedTouches[0];
        if (!touch) return;
        const distanceX = touch.clientX - touchStartX;
        const distanceY = touch.clientY - touchStartY;
        if (Math.abs(distanceX) < 55 || Math.abs(distanceX) < Math.abs(distanceY)) return;
        this.show(this.activeIndex + (distanceX < 0 ? 1 : -1));
      }, { passive: true });

      if (this.stage && this.items.length > 1) {
        this.galleryFrame = 0;
        this.updateCurrentFromScroll = () => {
          if (this.galleryFrame) return;
          this.galleryFrame = window.requestAnimationFrame(() => {
            this.galleryFrame = 0;
            if (this.isProgrammaticScroll) return;
            const maxScroll = Math.max(0, this.stage.scrollWidth - this.stage.clientWidth);
            if (maxScroll <= 1) {
              this.setMobileCurrent(0);
              return;
            }
            const progress = Math.min(1, Math.abs(this.stage.scrollLeft) / maxScroll);
            const index = Math.round(progress * (this.items.length - 1));
            this.setMobileCurrent(index);
          });
        };
        this.stage.addEventListener('scroll', this.updateCurrentFromScroll, { passive: true });
        window.addEventListener('resize', this.updateCurrentFromScroll, { passive: true });
        this.updateCurrentFromScroll();
      }
    }

    disconnectedCallback() {
      if (this.updateCurrentFromScroll) {
        this.stage?.removeEventListener('scroll', this.updateCurrentFromScroll);
        window.removeEventListener('resize', this.updateCurrentFromScroll);
      }
      if (this.galleryFrame) window.cancelAnimationFrame(this.galleryFrame);
      window.clearTimeout(this.programmaticScrollTimer);
    }

    open(index) {
      if (!this.dialog) return;
      this.lastOpener = this.items[index] || null;
      this.show(index);
      try {
        if (typeof this.dialog.showModal === 'function' && !this.dialog.open) this.dialog.showModal();
        else this.dialog.setAttribute('open', '');
      } catch (error) {
        this.dialog.setAttribute('open', '');
      }
      document.documentElement.classList.add('trips-gallery-open');
    }

    close() {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function' && this.dialog.open) this.dialog.close();
      else {
        this.dialog.removeAttribute('open');
        document.documentElement.classList.remove('trips-gallery-open');
        this.lastOpener?.focus?.({ preventScroll: true });
        this.lastOpener = null;
      }
    }

    show(index) {
      const total = this.items.length;
      if (!total || !this.lightboxImage) return;
      this.activeIndex = (index + total) % total;
      const item = this.items[this.activeIndex];

      this.lightboxImage.src = item.dataset.gallerySrc || '';
      this.lightboxImage.srcset = item.dataset.gallerySrcset || '';
      this.lightboxImage.sizes = 'min(92vw, 1800px)';
      this.lightboxImage.alt = item.dataset.galleryAlt || '';
      if (this.lightboxCaption) {
        this.lightboxCaption.textContent = item.dataset.galleryCaption || '';
        this.lightboxCaption.hidden = !item.dataset.galleryCaption;
      }
      if (this.lightboxCurrent) this.lightboxCurrent.textContent = String(this.activeIndex + 1);

      if (!this.reduceMotion) {
        this.lightboxImage.animate(
          [{ opacity: 0.3, transform: 'scale(0.985)' }, { opacity: 1, transform: 'scale(1)' }],
          { duration: 220, easing: 'cubic-bezier(.2,.75,.3,1)' }
        );
      }
    }

    scrollTo(index) {
      const total = this.items.length;
      if (!total) return;
      const targetIndex = Math.max(0, Math.min(index, total - 1));
      const maxScroll = Math.max(0, this.stage.scrollWidth - this.stage.clientWidth);
      if (maxScroll <= 1) {
        this.setMobileCurrent(0);
        return;
      }

      const progress = total > 1 ? targetIndex / (total - 1) : 0;
      const isRtl = window.getComputedStyle(this.stage).direction === 'rtl';
      this.isProgrammaticScroll = true;
      window.clearTimeout(this.programmaticScrollTimer);
      this.stage.scrollTo({
        left: maxScroll * progress * (isRtl ? -1 : 1),
        behavior: this.reduceMotion ? 'auto' : 'smooth'
      });
      this.setMobileCurrent(targetIndex);
      this.programmaticScrollTimer = window.setTimeout(() => {
        this.isProgrammaticScroll = false;
        this.updateCurrentFromScroll?.();
      }, this.reduceMotion ? 0 : 520);
    }

    setMobileCurrent(index) {
      this.activeIndex = index;
      if (this.mobileCurrent) this.mobileCurrent.textContent = String(index + 1);
      if (this.scrollPrevious) this.scrollPrevious.disabled = index <= 0;
      if (this.scrollNext) this.scrollNext.disabled = index >= this.items.length - 1;
    }
  });
}

if (!window.tripsReadMoreReady) {
  window.tripsReadMoreReady = true;
  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.classList.contains('trips-read-more') || !details.open) return;
    const content = details.querySelector('.trips-read-more__content');
    if (!content || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    content.animate(
      [{ opacity: 0, transform: 'translateY(-10px)' }, { opacity: 1, transform: 'translateY(0)' }],
      { duration: 260, easing: 'cubic-bezier(.2,.75,.3,1)' }
    );
  }, true);
}

if (!customElements.get('trips-contact-modal')) {
  customElements.define('trips-contact-modal', class TripsContactModal extends HTMLElement {
    connectedCallback() {
      if (this.dataset.tripsContactInitialized === 'true') return;
      this.dataset.tripsContactInitialized = 'true';
      this.dialog = this.querySelector('[data-trips-contact-dialog]');
      this.tripLabel = this.querySelector('[data-trips-contact-trip]');
      this.defaultTripLabel = this.tripLabel?.textContent || '';

      this.querySelectorAll('[data-trips-contact-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog?.addEventListener('close', () => this.afterClose());

      this.addEventListener('shopify:block:select', () => this.open(null, true));
    }

    open(opener, allowSetup = false) {
      if (!this.dialog) return false;
      const canPreviewSetup = Boolean(window.Shopify?.designMode);
      if (!allowSetup && !canPreviewSetup && this.dataset.tripsContactReady !== 'true') return false;

      this.lastOpener = opener || null;
      const context = opener?.dataset.tripsContactContext || this.defaultTripLabel;
      if (this.tripLabel && context) this.tripLabel.textContent = context.replace(/\s+/g, ' ').trim();

      try {
        if (typeof this.dialog.showModal === 'function' && !this.dialog.open) this.dialog.showModal();
        else this.dialog.setAttribute('open', '');
      } catch (error) {
        this.dialog.setAttribute('open', '');
      }

      document.documentElement.classList.add('trips-contact-open');
      opener?.setAttribute('aria-expanded', 'true');
      window.setTimeout(() => {
        const firstField = this.dialog.querySelector('[data-trips-contact-form] input:not([type="hidden"]), [data-trips-contact-form] select, [data-trips-contact-form] textarea')
          || this.dialog.querySelector('[data-trips-contact-close]');
        firstField?.focus({ preventScroll: true });
      }, 80);
      return true;
    }

    close() {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function' && this.dialog.open) this.dialog.close();
      else {
        this.dialog.removeAttribute('open');
        this.afterClose();
      }
    }

    afterClose() {
      document.documentElement.classList.remove('trips-contact-open');
      this.lastOpener?.setAttribute('aria-expanded', 'false');
      this.lastOpener?.focus?.({ preventScroll: true });
      this.lastOpener = null;
    }
  });
}

if (!window.tripsContactTriggerReady) {
  window.tripsContactTriggerReady = true;
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-trips-contact-open], a[href="#trip-enquiry"]');
    if (!opener) return;
    const modal = document.querySelector('trips-contact-modal');
    if (!modal || !modal.open(opener)) return;
    event.preventDefault();
  });
  document.addEventListener('shopify:section:select', (event) => {
    event.target?.querySelector?.('trips-contact-modal')?.open(null, true);
  });
}

if (!customElements.get('trips-compare')) {
  customElements.define('trips-compare', class TripsCompare extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.tabs = Array.from(this.querySelectorAll('[data-trips-compare-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-trips-compare-panel]'));
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.activeIndex = Math.max(0, this.tabs.findIndex((tab) => tab.classList.contains('is-active')));
      this.closest('.trips-comparison')?.classList.add('is-enhanced');

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.activate(index, true));
        tab.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let target = index;
          if (event.key === 'ArrowLeft') target = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'ArrowRight') target = (index + 1) % this.tabs.length;
          if (event.key === 'Home') target = 0;
          if (event.key === 'End') target = this.tabs.length - 1;
          this.activate(target, true);
        });
      });

      this.handleBreakpoint = () => this.syncAccessibility();
      this.mobileQuery.addEventListener?.('change', this.handleBreakpoint);
      this.activate(this.activeIndex, false);
    }

    disconnectedCallback() {
      this.mobileQuery?.removeEventListener?.('change', this.handleBreakpoint);
    }

    activate(index, focusTab) {
      if (!this.tabs.length) return;
      this.activeIndex = Math.max(0, Math.min(index, this.tabs.length - 1));
      this.tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === this.activeIndex;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach((panel, panelIndex) => panel.classList.toggle('is-active', panelIndex === this.activeIndex));
      this.syncAccessibility();
      if (focusTab) this.tabs[this.activeIndex]?.focus({ preventScroll: true });
    }

    syncAccessibility() {
      const mobile = this.mobileQuery?.matches;
      this.panels.forEach((panel, panelIndex) => {
        panel.setAttribute('aria-hidden', String(Boolean(mobile && panelIndex !== this.activeIndex)));
      });
    }
  });
}

if (!window.TripsUI) {
  window.TripsUI = {
    parallaxItems: new Set(),
    parallaxFrame: null,

    init(root = document) {
      this.initReveals(root);
      this.initTimelines(root);
      this.initTopography(root);
      this.initParallax(root);
    },

    getMotionObserver() {
      if (this.motionObserver) return this.motionObserver;
      if (!('IntersectionObserver' in window)) return null;

      this.motionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (entry.target.hasAttribute('data-trips-timeline')) {
            entry.target.classList.add('is-riding');
          } else if (entry.target.hasAttribute('data-trips-topography')) {
            entry.target.classList.add('is-active');
          } else {
            entry.target.classList.add('is-visible');
          }
          this.motionObserver.unobserve(entry.target);
        });
      }, { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });

      return this.motionObserver;
    },

    initReveals(root) {
      const elements = root.querySelectorAll('[data-trips-reveal]:not([data-trips-reveal-ready])');
      const observer = this.getMotionObserver();

      elements.forEach((element) => {
        element.setAttribute('data-trips-reveal-ready', '');
        const delay = Number.parseInt(element.dataset.tripsDelay || '0', 10);
        element.style.setProperty('--trips-reveal-delay', `${Math.max(0, delay)}ms`);

        if (!observer || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          element.classList.add('is-visible');
        } else {
          observer.observe(element);
        }
      });
    },

    initTimelines(root) {
      const timelines = root.querySelectorAll('[data-trips-timeline]:not([data-trips-timeline-ready])');
      const observer = this.getMotionObserver();

      timelines.forEach((timeline) => {
        timeline.setAttribute('data-trips-timeline-ready', '');
        if (!observer || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          timeline.classList.add('is-riding');
        } else {
          observer.observe(timeline);
        }
      });
    },

    initTopography(root) {
      const fields = root.querySelectorAll('[data-trips-topography]:not([data-trips-topography-ready])');
      const observer = this.getMotionObserver();

      fields.forEach((field) => {
        field.setAttribute('data-trips-topography-ready', '');
        if (!observer || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          field.classList.add('is-active');
        } else {
          observer.observe(field);
        }
      });
    },

    initParallax(root) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const items = root.querySelectorAll('[data-trips-parallax]:not([data-trips-parallax-ready])');

      items.forEach((item) => {
        item.setAttribute('data-trips-parallax-ready', '');
        item.tripsParallaxImage = item.querySelector('.trips-hero__image, .trips-media-layer__image, img');
        if (!item.tripsParallaxImage) return;
        this.parallaxItems.add(item);
      });

      if (!this.parallaxBound && this.parallaxItems.size) {
        this.parallaxBound = true;
        this.requestParallax = this.requestParallax.bind(this);
        window.addEventListener('scroll', this.requestParallax, { passive: true });
        window.addEventListener('resize', this.requestParallax, { passive: true });
      }
      this.requestParallax();
    },

    requestParallax() {
      if (this.parallaxFrame) return;
      this.parallaxFrame = window.requestAnimationFrame(() => {
        this.parallaxFrame = null;
        this.updateParallax();
      });
    },

    updateParallax() {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      this.parallaxItems.forEach((item) => {
        if (!item.isConnected) {
          this.parallaxItems.delete(item);
          return;
        }
        const rect = item.getBoundingClientRect();
        if (rect.bottom < -100 || rect.top > viewportHeight + 100) return;
        const strength = Number.parseFloat(item.dataset.tripsParallaxStrength || '35');
        const progress = (viewportHeight - rect.top) / (viewportHeight + rect.height);
        const offset = (progress - 0.5) * strength * 2;
        item.tripsParallaxImage.style.setProperty('--trips-parallax-y', `${offset.toFixed(2)}px`);
      });
    }
  };

  document.addEventListener('shopify:section:load', (event) => window.TripsUI.init(event.target));
}

window.TripsUI.init();
